// GENERATED by blackbird_backend/scripts/tools/generate-discipline-registry.mjs. Do not edit.
// Source: blackbird_backend/src/layers/shared-utils/nodejs/clinical/templates.js (Phase 20B)

export type TemplateFieldType = 'text' | 'textarea' | 'select' | 'multiselect' | 'date' | 'number' | 'checkbox' | 'icd10';

export interface TemplateField {
  /** Care plans: the data key. Assessments store "<Section>-<Field>" in data.categories. */
  key?: string;
  name: string;
  type: TemplateFieldType;
  required?: boolean;
  options?: readonly string[];
  placeholder?: string;
  /** Assessment types the field shows for (initial, recertification, discharge) */
  showFor?: readonly string[];
}

export interface TemplateSection {
  name: string;
  /** assessmentType === 'recertification' style condition */
  when?: string;
  fields: readonly TemplateField[];
}

export interface ClinicalTemplate {
  id: string;
  version: number;
  kind: 'assessment' | 'carePlan';
  discipline: string;
  title: string;
  authors: readonly string[];
  assessmentTypes?: readonly string[];
  goalDomains?: readonly string[];
  sections: readonly TemplateSection[];
  cms485?: Record<string, unknown>;
}

export const CLINICAL_TEMPLATES: readonly ClinicalTemplate[] = [
  {
    "id": "MSW_PSYCHOSOCIAL",
    "version": 1,
    "kind": "assessment",
    "discipline": "MSW",
    "title": "Medical Social Work Psychosocial Assessment",
    "authors": [
      "MSW",
      "LCSW"
    ],
    "assessmentTypes": [
      "initial",
      "recertification",
      "discharge"
    ],
    "sections": [
      {
        "name": "Recertification Summary",
        "when": "assessmentType === 'recertification'",
        "fields": [
          {
            "name": "Changes Since Last Assessment",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Progress Toward Goals",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Discharge Summary",
        "when": "assessmentType === 'discharge'",
        "fields": [
          {
            "name": "Reason for MSW Discharge",
            "type": "select",
            "required": true,
            "options": [
              "Goals met",
              "Patient discharged from agency",
              "Family declined further services",
              "Transferred to another provider",
              "Other"
            ]
          },
          {
            "name": "Goals Met",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Follow-up and Referrals at Discharge",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Referral and Reason",
        "fields": [
          {
            "name": "Reason for MSW Referral",
            "type": "textarea",
            "required": true,
            "showFor": [
              "initial"
            ]
          },
          {
            "name": "Referred By",
            "type": "select",
            "showFor": [
              "initial"
            ],
            "options": [
              "RN",
              "PT / OT / ST",
              "Physician",
              "Family",
              "Health plan or MCO case manager",
              "Other"
            ]
          },
          {
            "name": "Visit Date",
            "type": "date",
            "required": true
          }
        ]
      },
      {
        "name": "Family and Household",
        "fields": [
          {
            "name": "Household Members and Relationships",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Primary Caregivers",
            "type": "text",
            "required": true
          },
          {
            "name": "Caregiver Availability and Backup Plan",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Preferred Language and Interpreter Needs",
            "type": "text"
          },
          {
            "name": "Cultural or Religious Considerations",
            "type": "textarea"
          }
        ]
      },
      {
        "name": "Caregiver Coping and Stress",
        "fields": [
          {
            "name": "Caregiver Stress Level",
            "type": "select",
            "required": true,
            "options": [
              "Low",
              "Moderate",
              "High",
              "Crisis"
            ]
          },
          {
            "name": "Signs of Caregiver Strain",
            "type": "multiselect",
            "options": [
              "Sleep deprivation",
              "Missed work or job loss",
              "Social isolation",
              "Caregiver health problems",
              "Conflict in the household",
              "None observed"
            ]
          },
          {
            "name": "Respite and Support Needs",
            "type": "textarea"
          }
        ]
      },
      {
        "name": "Financial and Benefits",
        "fields": [
          {
            "name": "Housing Stability",
            "type": "select",
            "required": true,
            "options": [
              "Stable",
              "At risk",
              "Unstable or homeless"
            ]
          },
          {
            "name": "Benefits in Place",
            "type": "multiselect",
            "options": [
              "Medicaid",
              "SSI",
              "SNAP",
              "WIC",
              "TANF",
              "MDCP waiver",
              "CLASS waiver",
              "Private insurance",
              "Other"
            ]
          },
          {
            "name": "Benefit Gaps or Applications Needed",
            "type": "textarea"
          },
          {
            "name": "Transportation to Appointments",
            "type": "select",
            "options": [
              "Reliable",
              "Unreliable",
              "None",
              "Uses Medicaid transportation (MTP)"
            ]
          }
        ]
      },
      {
        "name": "Home Environment and Safety",
        "fields": [
          {
            "name": "Home Suitable for Equipment and Care",
            "type": "select",
            "required": true,
            "options": [
              "Yes",
              "Partly",
              "No"
            ]
          },
          {
            "name": "Reliable Power for Medical Equipment",
            "type": "select",
            "options": [
              "Yes",
              "No",
              "Not applicable"
            ]
          },
          {
            "name": "Emergency Plan in Place",
            "type": "select",
            "options": [
              "Yes",
              "No"
            ]
          },
          {
            "name": "Home Safety Concerns",
            "type": "textarea"
          }
        ]
      },
      {
        "name": "Education and Development",
        "fields": [
          {
            "name": "School or Early Intervention",
            "type": "select",
            "options": [
              "Early Childhood Intervention (ECI)",
              "School with special education services",
              "Homebound instruction",
              "Not enrolled",
              "Not applicable"
            ]
          },
          {
            "name": "Educational or Developmental Needs",
            "type": "textarea"
          }
        ]
      },
      {
        "name": "Risk Screening",
        "fields": [
          {
            "name": "Abuse or Neglect Concerns",
            "type": "select",
            "required": true,
            "options": [
              "None identified",
              "Concern under review",
              "Reported to DFPS"
            ]
          },
          {
            "name": "Domestic Violence Screening",
            "type": "select",
            "options": [
              "Negative",
              "Positive",
              "Declined"
            ]
          },
          {
            "name": "Substance Use in Household",
            "type": "select",
            "options": [
              "None reported",
              "Concern",
              "Declined"
            ]
          },
          {
            "name": "Mental Health Concerns (Patient or Caregiver)",
            "type": "textarea"
          }
        ]
      },
      {
        "name": "Resources and Referrals",
        "fields": [
          {
            "name": "Resources Discussed",
            "type": "multiselect",
            "options": [
              "Parent support group",
              "Respite care",
              "Medicaid transportation (MTP)",
              "Utility assistance",
              "Housing assistance",
              "Legal aid or guardianship",
              "Mental health services",
              "Palliative or hospice care",
              "Other"
            ]
          },
          {
            "name": "Referrals Made",
            "type": "textarea"
          }
        ]
      },
      {
        "name": "Advance Care Planning",
        "fields": [
          {
            "name": "Advance Care Planning Discussed",
            "type": "select",
            "options": [
              "Yes",
              "Not yet",
              "Declined",
              "Not applicable"
            ]
          },
          {
            "name": "Decision-Maker or Guardianship Issues",
            "type": "textarea"
          }
        ]
      },
      {
        "name": "Assessment Summary",
        "fields": [
          {
            "name": "Psychosocial Summary",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Recommended MSW Frequency",
            "type": "text",
            "required": true,
            "showFor": [
              "initial",
              "recertification"
            ],
            "placeholder": "e.g. 1 visit every 2 weeks for 60 days"
          }
        ]
      },
      {
        "name": "MSW Attestation",
        "fields": [
          {
            "name": "MSW Signature Attestation",
            "type": "select",
            "required": true,
            "options": [
              "I attest this record is complete and accurate"
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "MSW_CARE_PLAN",
    "version": 1,
    "kind": "carePlan",
    "discipline": "MSW",
    "title": "Medical Social Work Care Plan",
    "authors": [
      "MSW",
      "LCSW"
    ],
    "goalDomains": [
      "Caregiver coping",
      "Financial and benefits",
      "Community resources",
      "Home safety",
      "Advance care planning",
      "Education and development",
      "Other"
    ],
    "cms485": {
      "frequency": "mswFrequency",
      "duration": "mswDuration",
      "interventions": [
        "mswInterventions",
        "interventionDetails"
      ],
      "medicalNecessity": "mswNeedReason",
      "dischargePlan": "dischargePlan"
    },
    "sections": [
      {
        "name": "Orders",
        "fields": [
          {
            "key": "mswFrequency",
            "name": "MSW Visit Frequency",
            "type": "text",
            "required": true,
            "placeholder": "e.g. 1 visit every 2 weeks"
          },
          {
            "key": "mswDuration",
            "name": "Duration",
            "type": "text",
            "required": true,
            "placeholder": "e.g. 60 days"
          },
          {
            "key": "orderingPhysicianName",
            "name": "Ordering Physician",
            "type": "text",
            "required": true
          },
          {
            "key": "orderingPhysicianNPI",
            "name": "Ordering Physician NPI",
            "type": "text"
          }
        ]
      },
      {
        "name": "Problems and Interventions",
        "fields": [
          {
            "key": "mswNeedReason",
            "name": "Reason Social Work Services Are Needed",
            "type": "textarea",
            "required": true
          },
          {
            "key": "psychosocialProblems",
            "name": "Psychosocial Problems Addressed",
            "type": "textarea",
            "required": true
          },
          {
            "key": "mswInterventions",
            "name": "Interventions",
            "type": "multiselect",
            "required": true,
            "options": [
              "Short-term counseling",
              "Caregiver support",
              "Benefits and financial assistance",
              "Community resource linkage",
              "Crisis intervention",
              "Advance care planning",
              "School or ECI coordination",
              "Discharge planning"
            ]
          },
          {
            "key": "interventionDetails",
            "name": "Intervention Details",
            "type": "textarea"
          }
        ]
      },
      {
        "name": "Coordination and Discharge",
        "fields": [
          {
            "key": "careCoordination",
            "name": "Care Coordination with the Team",
            "type": "textarea"
          },
          {
            "key": "dischargePlan",
            "name": "Discharge Plan",
            "type": "textarea",
            "required": true
          }
        ]
      }
    ]
  },
  {
    "id": "HHA_AIDE_CARE_PLAN",
    "version": 1,
    "kind": "carePlan",
    "discipline": "HHA",
    "title": "Home Health Aide Care Plan",
    "authors": [
      "RN",
      "PT",
      "OT"
    ],
    "goalDomains": [
      "Personal care",
      "Safety",
      "Mobility",
      "Nutrition",
      "Skin integrity",
      "Caregiver relief",
      "Other"
    ],
    "cms485": {
      "frequency": "aideFrequency",
      "duration": "aideDuration",
      "interventions": [
        "aideTasks",
        "taskInstructions"
      ],
      "medicalNecessity": "aideNeedReason",
      "dischargePlan": null
    },
    "sections": [
      {
        "name": "Aide Assignment",
        "fields": [
          {
            "key": "aideFrequency",
            "name": "Aide Visit Frequency",
            "type": "text",
            "required": true,
            "placeholder": "e.g. 3 visits per week"
          },
          {
            "key": "aideVisitLength",
            "name": "Visit Length",
            "type": "select",
            "required": true,
            "options": [
              "Up to 1 hour",
              "1 to 2 hours",
              "2 to 2.5 hours"
            ]
          },
          {
            "key": "aideDuration",
            "name": "Duration",
            "type": "text",
            "required": true,
            "placeholder": "e.g. 60 days"
          },
          {
            "key": "aideNeedReason",
            "name": "Reason Aide Services Are Needed",
            "type": "textarea",
            "required": true
          },
          {
            "key": "supervisingClinician",
            "name": "Supervising Clinician",
            "type": "text",
            "required": true
          },
          {
            "key": "supervisionInterval",
            "name": "Supervisory Visit Interval",
            "type": "select",
            "required": true,
            "options": [
              "Every 14 days (aide works with skilled care)",
              "Every 60 days (aide services only)"
            ]
          }
        ]
      },
      {
        "name": "Assigned Tasks",
        "fields": [
          {
            "key": "aideTasks",
            "name": "Personal Care Tasks",
            "type": "multiselect",
            "required": true,
            "options": [
              "Bathing (tub or shower)",
              "Bed bath",
              "Oral care",
              "Hair care",
              "Skin care",
              "Toileting or diapering",
              "Dressing",
              "Feeding assistance",
              "Positioning and turning",
              "Transfers",
              "Ambulation assistance",
              "Range of motion as instructed",
              "Light housekeeping (patient area)",
              "Patient laundry",
              "Meal preparation for the patient"
            ]
          },
          {
            "key": "taskInstructions",
            "name": "Task Instructions",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Precautions and Reporting",
        "fields": [
          {
            "key": "aidePrecautions",
            "name": "Precautions",
            "type": "textarea",
            "required": true,
            "placeholder": "Allergies, seizure precautions, aspiration precautions, equipment the aide must not adjust"
          },
          {
            "key": "reportToSupervisor",
            "name": "Report to the Supervising Clinician",
            "type": "textarea",
            "required": true,
            "placeholder": "Changes in skin, breathing, intake or behavior; falls; equipment problems"
          },
          {
            "key": "equipmentUsed",
            "name": "Equipment Used",
            "type": "textarea"
          }
        ]
      }
    ]
  },
  {
    "id": "ATTENDANT_SERVICE_PLAN",
    "version": 1,
    "kind": "carePlan",
    "discipline": "ATTENDANT",
    "title": "Personal Care / CFC Attendant Service Plan",
    "authors": [
      "RN",
      "PT",
      "OT"
    ],
    "goalDomains": [
      "Personal care",
      "Habilitation skills",
      "Safety",
      "Mobility",
      "Nutrition",
      "Caregiver relief",
      "Other"
    ],
    "cms485": null,
    "sections": [
      {
        "name": "Authorization",
        "fields": [
          {
            "key": "attendantProgram",
            "name": "Programme",
            "type": "select",
            "required": true,
            "options": [
              "Personal care services (PCS)",
              "CFC personal assistance services",
              "CFC personal assistance and habilitation"
            ]
          },
          {
            "key": "authorizedHoursPerWeek",
            "name": "Authorized Hours per Week",
            "type": "number",
            "required": true
          },
          {
            "key": "serviceSchedule",
            "name": "Service Schedule",
            "type": "textarea",
            "required": true,
            "placeholder": "Days and times the attendant works"
          },
          {
            "key": "supervisingClinician",
            "name": "Supervising Clinician",
            "type": "text",
            "required": true
          }
        ]
      },
      {
        "name": "Tasks",
        "fields": [
          {
            "key": "attendantTasks",
            "name": "Personal Assistance Tasks",
            "type": "multiselect",
            "required": true,
            "options": [
              "Bathing",
              "Dressing",
              "Grooming",
              "Oral care",
              "Toileting or diapering",
              "Feeding assistance",
              "Meal preparation",
              "Transfers",
              "Positioning",
              "Ambulation assistance",
              "Exercise as instructed",
              "Light housekeeping",
              "Laundry",
              "Shopping or errands",
              "Escort to appointments",
              "Medication reminders"
            ]
          },
          {
            "key": "habilitationTasks",
            "name": "Habilitation Skills (CFC HAB)",
            "type": "multiselect",
            "options": [
              "Self-care skills",
              "Communication skills",
              "Socialization",
              "Community integration",
              "Behavior support as planned",
              "Money management",
              "Household skills"
            ]
          },
          {
            "key": "taskInstructions",
            "name": "Task Instructions",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Backup and Safety",
        "fields": [
          {
            "key": "backupPlan",
            "name": "Backup Plan When the Attendant Cannot Come",
            "type": "textarea",
            "required": true
          },
          {
            "key": "emergencyPlan",
            "name": "Emergency Plan",
            "type": "textarea",
            "required": true
          },
          {
            "key": "attendantPrecautions",
            "name": "Precautions",
            "type": "textarea",
            "required": true
          },
          {
            "key": "reportToSupervisor",
            "name": "Report to the Supervising Clinician",
            "type": "textarea",
            "required": true
          }
        ]
      }
    ]
  },
  {
    "id": "RT_ASSESSMENT",
    "version": 1,
    "kind": "assessment",
    "discipline": "RT",
    "title": "Respiratory Therapy Assessment",
    "authors": [
      "RT"
    ],
    "assessmentTypes": [
      "initial",
      "recertification",
      "discharge"
    ],
    "sections": [
      {
        "name": "Recertification Summary",
        "when": "assessmentType === 'recertification'",
        "fields": [
          {
            "name": "Changes Since Last Assessment",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Progress Toward Goals",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Discharge Summary",
        "when": "assessmentType === 'discharge'",
        "fields": [
          {
            "name": "Reason for RT Discharge",
            "type": "select",
            "required": true,
            "options": [
              "Goals met",
              "Patient discharged from agency",
              "Family declined further services",
              "Transferred to another provider",
              "Other"
            ]
          },
          {
            "name": "Goals Met",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Follow-up and Referrals at Discharge",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Respiratory History",
        "fields": [
          {
            "name": "Respiratory Diagnoses",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Airway",
            "type": "select",
            "required": true,
            "options": [
              "Natural airway",
              "Tracheostomy",
              "Other"
            ]
          },
          {
            "name": "Tracheostomy Size and Type",
            "type": "text"
          },
          {
            "name": "Ventilator Dependence",
            "type": "select",
            "required": true,
            "options": [
              "None",
              "Night only",
              "Part-time",
              "Full-time"
            ]
          },
          {
            "name": "Ventilator Settings",
            "type": "textarea"
          },
          {
            "name": "Oxygen",
            "type": "select",
            "required": true,
            "options": [
              "None",
              "As needed",
              "Continuous"
            ]
          },
          {
            "name": "Oxygen Flow",
            "type": "text"
          }
        ]
      },
      {
        "name": "Respiratory Assessment",
        "fields": [
          {
            "name": "Breath Sounds",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Work of Breathing",
            "type": "select",
            "required": true,
            "options": [
              "Normal",
              "Mild distress",
              "Moderate distress",
              "Severe distress"
            ]
          },
          {
            "name": "SpO2 at Rest",
            "type": "number"
          },
          {
            "name": "Secretions",
            "type": "select",
            "options": [
              "None",
              "Small",
              "Moderate",
              "Large"
            ]
          },
          {
            "name": "Cough Effectiveness",
            "type": "select",
            "options": [
              "Effective",
              "Weak",
              "Absent"
            ]
          }
        ]
      },
      {
        "name": "Equipment",
        "fields": [
          {
            "name": "Equipment in Home",
            "type": "multiselect",
            "required": true,
            "options": [
              "Ventilator",
              "Oxygen concentrator",
              "Oxygen tanks",
              "Suction machine",
              "Pulse oximeter",
              "Nebulizer",
              "Cough assist",
              "Heated humidifier",
              "Apnea monitor",
              "BiPAP or CPAP",
              "None"
            ]
          },
          {
            "name": "Equipment Check Findings",
            "type": "textarea"
          },
          {
            "name": "DME Supplier",
            "type": "text"
          }
        ]
      },
      {
        "name": "Caregiver Competency",
        "fields": [
          {
            "name": "Caregiver Competencies Verified",
            "type": "multiselect",
            "options": [
              "Tracheostomy care",
              "Routine tracheostomy change",
              "Emergency tracheostomy change",
              "Suctioning",
              "Ventilator alarms",
              "CPR",
              "Nebulizer treatments",
              "Cough assist",
              "Oxygen safety"
            ]
          },
          {
            "name": "Caregiver Training Needs",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Asthma",
        "fields": [
          {
            "name": "Asthma Control",
            "type": "select",
            "options": [
              "Well controlled",
              "Not well controlled",
              "Very poorly controlled",
              "Not applicable"
            ]
          },
          {
            "name": "Asthma Action Plan on File",
            "type": "select",
            "options": [
              "Yes",
              "No",
              "Not applicable"
            ]
          },
          {
            "name": "Triggers",
            "type": "textarea"
          }
        ]
      }
    ]
  },
  {
    "id": "RT_CARE_PLAN",
    "version": 1,
    "kind": "carePlan",
    "discipline": "RT",
    "title": "Respiratory Therapy Care Plan",
    "authors": [
      "RT"
    ],
    "goalDomains": [
      "Airway clearance",
      "Oxygenation",
      "Ventilator management",
      "Tracheostomy care",
      "Asthma control",
      "Caregiver education",
      "Other"
    ],
    "cms485": {
      "frequency": "rtFrequency",
      "duration": "rtDuration",
      "interventions": [
        "rtInterventions",
        "rtInstructions"
      ],
      "medicalNecessity": "rtNeedReason",
      "dischargePlan": "dischargePlan"
    },
    "sections": [
      {
        "name": "Orders",
        "fields": [
          {
            "key": "rtFrequency",
            "name": "RT Visit Frequency",
            "type": "text",
            "required": true,
            "placeholder": "e.g. 1 visit per week"
          },
          {
            "key": "rtDuration",
            "name": "Duration",
            "type": "text",
            "required": true,
            "placeholder": "e.g. 60 days"
          },
          {
            "key": "rtNeedReason",
            "name": "Reason RT Services Are Needed",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Interventions",
        "fields": [
          {
            "key": "rtInterventions",
            "name": "Interventions",
            "type": "multiselect",
            "required": true,
            "options": [
              "Airway clearance",
              "Chest physiotherapy",
              "Nebulizer treatments",
              "Ventilator management and checks",
              "Tracheostomy care and change",
              "Oxygen titration",
              "Cough assist",
              "Asthma education",
              "Equipment check and troubleshooting",
              "Caregiver training"
            ]
          },
          {
            "key": "rtInstructions",
            "name": "Instructions",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Safety and Discharge",
        "fields": [
          {
            "key": "emergencyPlan",
            "name": "Respiratory Emergency Plan",
            "type": "textarea",
            "required": true
          },
          {
            "key": "dischargePlan",
            "name": "Discharge Plan",
            "type": "textarea"
          }
        ]
      }
    ]
  },
  {
    "id": "RD_NUTRITION_ASSESSMENT",
    "version": 1,
    "kind": "assessment",
    "discipline": "RD",
    "title": "Nutrition Assessment",
    "authors": [
      "RD"
    ],
    "assessmentTypes": [
      "initial",
      "recertification",
      "discharge"
    ],
    "sections": [
      {
        "name": "Recertification Summary",
        "when": "assessmentType === 'recertification'",
        "fields": [
          {
            "name": "Changes Since Last Assessment",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Progress Toward Goals",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Discharge Summary",
        "when": "assessmentType === 'discharge'",
        "fields": [
          {
            "name": "Reason for Nutrition Discharge",
            "type": "select",
            "required": true,
            "options": [
              "Goals met",
              "Patient discharged from agency",
              "Family declined further services",
              "Transferred to another provider",
              "Other"
            ]
          },
          {
            "name": "Goals Met",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Follow-up and Referrals at Discharge",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Anthropometrics",
        "fields": [
          {
            "name": "Weight",
            "type": "text",
            "required": true,
            "placeholder": "kg, with the date measured"
          },
          {
            "name": "Length or Height",
            "type": "text",
            "required": true
          },
          {
            "name": "Weight-for-Length or BMI Percentile",
            "type": "text"
          },
          {
            "name": "Growth Trend",
            "type": "select",
            "required": true,
            "options": [
              "Following the curve",
              "Crossing percentiles down",
              "Crossing percentiles up",
              "Not assessed"
            ]
          }
        ]
      },
      {
        "name": "Feeding",
        "fields": [
          {
            "name": "Feeding Route",
            "type": "select",
            "required": true,
            "options": [
              "Oral",
              "G-tube",
              "GJ-tube",
              "NG tube",
              "NJ tube",
              "Combination"
            ]
          },
          {
            "name": "Formula or Diet",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Feeding Regimen",
            "type": "textarea",
            "required": true,
            "placeholder": "Volumes, rate, schedule"
          },
          {
            "name": "Free Water",
            "type": "text"
          }
        ]
      },
      {
        "name": "Intake and Tolerance",
        "fields": [
          {
            "name": "Estimated Intake Compared With Needs",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Tolerance",
            "type": "multiselect",
            "options": [
              "Tolerating well",
              "Vomiting",
              "Reflux",
              "Diarrhea",
              "Constipation",
              "Abdominal distension",
              "Feeding refusal"
            ]
          },
          {
            "name": "Labs Reviewed",
            "type": "textarea"
          }
        ]
      },
      {
        "name": "Nutrition Diagnosis",
        "fields": [
          {
            "name": "Nutrition Diagnosis",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Estimated Needs",
            "type": "textarea",
            "required": true,
            "placeholder": "Energy, protein, fluid"
          }
        ]
      }
    ]
  },
  {
    "id": "RD_CARE_PLAN",
    "version": 1,
    "kind": "carePlan",
    "discipline": "RD",
    "title": "Nutrition Care Plan",
    "authors": [
      "RD"
    ],
    "goalDomains": [
      "Growth",
      "Intake",
      "Tolerance",
      "Hydration",
      "Oral feeding",
      "Caregiver education",
      "Other"
    ],
    "cms485": {
      "frequency": "rdFrequency",
      "duration": "rdDuration",
      "interventions": [
        "rdInterventions",
        "rdRecommendations"
      ],
      "medicalNecessity": "rdNeedReason",
      "dischargePlan": "dischargePlan"
    },
    "sections": [
      {
        "name": "Orders",
        "fields": [
          {
            "key": "rdFrequency",
            "name": "Nutrition Visit Frequency",
            "type": "text",
            "required": true,
            "placeholder": "e.g. 1 visit per month"
          },
          {
            "key": "rdDuration",
            "name": "Duration",
            "type": "text",
            "required": true
          },
          {
            "key": "rdNeedReason",
            "name": "Reason Nutrition Services Are Needed",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Plan",
        "fields": [
          {
            "key": "rdRecommendations",
            "name": "Formula and Regimen Recommendations",
            "type": "textarea",
            "required": true
          },
          {
            "key": "rdInterventions",
            "name": "Interventions",
            "type": "multiselect",
            "required": true,
            "options": [
              "Formula change",
              "Regimen change",
              "Growth monitoring",
              "Caregiver education",
              "Coordinate with the RN feeding plan",
              "Coordinate with the physician",
              "Oral feeding plan with speech therapy"
            ]
          },
          {
            "key": "rdMonitoring",
            "name": "Monitoring",
            "type": "textarea",
            "required": true,
            "placeholder": "How often to weigh, what to report"
          },
          {
            "key": "dischargePlan",
            "name": "Discharge Plan",
            "type": "textarea"
          }
        ]
      }
    ]
  },
  {
    "id": "BH_ASSESSMENT",
    "version": 1,
    "kind": "assessment",
    "discipline": "BH",
    "title": "Behavioral Assessment",
    "authors": [
      "LPC",
      "LCSW",
      "BCBA"
    ],
    "assessmentTypes": [
      "initial",
      "recertification",
      "discharge"
    ],
    "sections": [
      {
        "name": "Recertification Summary",
        "when": "assessmentType === 'recertification'",
        "fields": [
          {
            "name": "Changes Since Last Assessment",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Progress Toward Goals",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Discharge Summary",
        "when": "assessmentType === 'discharge'",
        "fields": [
          {
            "name": "Reason for Behavioral Health Discharge",
            "type": "select",
            "required": true,
            "options": [
              "Goals met",
              "Patient discharged from agency",
              "Family declined further services",
              "Transferred to another provider",
              "Other"
            ]
          },
          {
            "name": "Goals Met",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Follow-up and Referrals at Discharge",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Referral",
        "fields": [
          {
            "name": "Reason for Referral",
            "type": "textarea",
            "required": true,
            "showFor": [
              "initial"
            ]
          },
          {
            "name": "Assessment Type",
            "type": "select",
            "required": true,
            "options": [
              "Functional behavior assessment",
              "Mental health assessment",
              "Developmental assessment"
            ]
          }
        ]
      },
      {
        "name": "Target Behaviors",
        "fields": [
          {
            "name": "Target Behaviors",
            "type": "textarea",
            "required": true,
            "placeholder": "Observable definitions"
          },
          {
            "name": "Frequency and Intensity",
            "type": "textarea",
            "required": true
          },
          {
            "name": "Antecedents",
            "type": "textarea"
          },
          {
            "name": "Consequences",
            "type": "textarea"
          },
          {
            "name": "Hypothesized Function",
            "type": "multiselect",
            "options": [
              "Attention",
              "Escape or avoidance",
              "Access to items or activities",
              "Sensory",
              "Unclear"
            ]
          }
        ]
      },
      {
        "name": "Skills",
        "fields": [
          {
            "name": "Communication",
            "type": "textarea"
          },
          {
            "name": "Daily Living Skills",
            "type": "textarea"
          },
          {
            "name": "Social Skills",
            "type": "textarea"
          }
        ]
      },
      {
        "name": "Risk and Safety",
        "fields": [
          {
            "name": "Safety Risks",
            "type": "multiselect",
            "required": true,
            "options": [
              "Self-injury",
              "Aggression",
              "Elopement",
              "Pica",
              "Property destruction",
              "None identified"
            ]
          },
          {
            "name": "Crisis Plan Needed",
            "type": "select",
            "required": true,
            "options": [
              "Yes",
              "No"
            ]
          },
          {
            "name": "Standardized Tools Used",
            "type": "textarea"
          }
        ]
      }
    ]
  },
  {
    "id": "BH_CARE_PLAN",
    "version": 1,
    "kind": "carePlan",
    "discipline": "BH",
    "title": "Behavior Plan",
    "authors": [
      "LPC",
      "LCSW",
      "BCBA"
    ],
    "goalDomains": [
      "Behavior reduction",
      "Communication",
      "Daily living skills",
      "Social skills",
      "Caregiver training",
      "Safety",
      "Other"
    ],
    "cms485": null,
    "sections": [
      {
        "name": "Service",
        "fields": [
          {
            "key": "bhFrequency",
            "name": "Session Frequency",
            "type": "text",
            "required": true,
            "placeholder": "e.g. 10 hours per week"
          },
          {
            "key": "bhDuration",
            "name": "Duration",
            "type": "text",
            "required": true
          },
          {
            "key": "bhNeedReason",
            "name": "Reason Services Are Needed",
            "type": "textarea",
            "required": true
          }
        ]
      },
      {
        "name": "Plan",
        "fields": [
          {
            "key": "replacementBehaviors",
            "name": "Replacement Behaviors",
            "type": "textarea",
            "required": true
          },
          {
            "key": "bhInterventions",
            "name": "Interventions",
            "type": "multiselect",
            "required": true,
            "options": [
              "Antecedent strategies",
              "Reinforcement plan",
              "Functional communication training",
              "Skill acquisition programmes",
              "Caregiver training",
              "Crisis plan",
              "Coordination with school or ECI"
            ]
          },
          {
            "key": "dataCollection",
            "name": "Data Collection",
            "type": "textarea",
            "required": true,
            "placeholder": "What is measured, how and how often"
          },
          {
            "key": "crisisPlan",
            "name": "Crisis Plan",
            "type": "textarea"
          },
          {
            "key": "dischargePlan",
            "name": "Discharge Plan",
            "type": "textarea"
          }
        ]
      }
    ]
  }
];
